import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { sentryCaptureError } from 'services/sentry/capture';
import { useAppSelector } from 'store/hooks';
import { cardFieldNameSelector } from 'store/slices/checkout';
import { selectedCardSelector } from 'store/slices/userCard';

import { verifyReservationCard } from '../api/reservationClient';
import { ReservationCardStatus } from '../types';
import { useIsReservationCardBlockVisible } from '../utils/reservationFlags';
import { useReservationVerificationSocket } from './useReservationVerificationSocket';

/**
 * Drives the inline card-verification block inside the deposit form.
 *
 * Lives here rather than in the form so that `CheckouForm` needs only a hook call and one render:
 * all the state, the upload and the field-readiness logic stay inside the feature.
 *
 * Self-contained by design — no dependency on `CardVerificationController`, the `kycVerification`
 * slice or `checkoutAPI`, all of which belong to the separate `kyc_card_verification` feature.
 */
export const useReservationCardVerification = () => {
  const isVisible = useIsReservationCardBlockVisible();
  const cardFieldName = useAppSelector(cardFieldNameSelector);
  const { selectedCard } = useAppSelector(selectedCardSelector);
  const {
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCardFieldFocused, setIsCardFieldFocused] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [status, setStatus] = useState<ReservationCardStatus | null>(null);
  const [hasAnswer, setHasAnswer] = useState(false);
  const [verifiedCardId, setVerifiedCardId] = useState<number | null>(selectedCard?.id ?? null);

  const cardFieldValue = watch(cardFieldName || '');
  const isStoredCard = !selectedCard?.isNew && !!selectedCard?.id;

  // A stored card arrives with its status on `GET /v1/user-cards/{amount}`; a freshly typed one has
  // none until the upload answers.
  const knownStatus = (selectedCard?.kyc_verification_status as ReservationCardStatus | null) ?? null;
  const currentStatus = status ?? (isStoredCard ? knownStatus : null);

  /**
   * The contract maps a `kyc_verification_status` of 3 *or* 4 to the red state. Status 3 is
   * ambiguous on its own, though — it is also what a stored card that has simply never been
   * confirmed reports — so it only reads as a rejection once the backend has actually answered
   * about this card. Same resolution `CardVerificationController` applies.
   */
  const effectiveStatus =
    currentStatus === ReservationCardStatus.notVerified && (hasAnswer || isStoredCard)
      ? ReservationCardStatus.failed
      : currentStatus;

  // Socket callback for async KYCaid result
  const handleVerificationResult = useCallback((resultStatus: ReservationCardStatus) => {
    setStatus(resultStatus);
    setHasAnswer(true);
    setCurrentFile(null);
  }, []);

  const { connect, disconnect } = useReservationVerificationSocket({
    cardId: verifiedCardId,
    onResult: handleVerificationResult,
  });

  // Connect socket if status is already IN_PROGRESS on mount (state restoration)
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (knownStatus === ReservationCardStatus.inProgress && !hasConnectedRef.current) {
      hasConnectedRef.current = true;
      connect();
    }
  }, [knownStatus, connect]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      disconnect();
    },
    [disconnect],
  );

  useEffect(() => {
    if (!cardFieldName) return undefined;

    const handleFocus = (event: FocusEvent, isFocused: boolean) => {
      const target = event.target as HTMLInputElement;

      if (target?.name === cardFieldName) {
        setIsCardFieldFocused(isFocused);
      }
    };

    const onFocusIn = (event: FocusEvent) => handleFocus(event, true);
    const onFocusOut = (event: FocusEvent) => handleFocus(event, false);

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [cardFieldName]);

  // A saved card is ready immediately; a freshly typed one only once it is valid and unfocused.
  const isCardReady =
    (!selectedCard?.isNew && !!selectedCard?.id) ||
    (!isCardFieldFocused && !!cardFieldName && !errors[cardFieldName] && !!cardFieldValue);

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (!files?.length) {
        setCurrentFile(null);
        return;
      }

      const file = files[0];
      const isExistingCard = !!(selectedCard?.id && !selectedCard?.isNew);

      setCurrentFile(file);
      setStatus(ReservationCardStatus.inProgress);
      setHasAnswer(false);

      // Open socket BEFORE the request to catch async KYCaid callback
      connect();

      verifyReservationCard({
        file,
        cardId: isExistingCard ? selectedCard?.id : undefined,
        cardPan: isExistingCard ? undefined : getValues(cardFieldName || ''),
        saveCard: !!getValues('save_card'),
      })
        .then((response) => {
          setVerifiedCardId(response.card_id);
          setStatus(response.kyc_verification_status);
          setHasAnswer(true);

          // If status is final (not IN_PROGRESS), close socket
          if (response.kyc_verification_status !== ReservationCardStatus.inProgress) {
            setCurrentFile(null);
            disconnect();
          }
          // If IN_PROGRESS — socket callback will handle the final result
        })
        .catch((error) => {
          sentryCaptureError(error, { loggerId: 'reservation-card-block' });
          setStatus(ReservationCardStatus.failed);
          setHasAnswer(true);
          setCurrentFile(null);
          disconnect();
        });

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [selectedCard, cardFieldName, getValues, connect, disconnect],
  );

  const onReset = useCallback(() => {
    setCurrentFile(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const onOpenPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return {
    isVisible,
    status: effectiveStatus,
    /** True while an upload is in flight — the card must not be edited mid-verification. */
    isPending: effectiveStatus === ReservationCardStatus.inProgress,
    isCardReady,
    inputRef,
    currentFile,
    onFileChange,
    onReset,
    onOpenPicker,
  };
};
